import { appState } from '../../store/appState.js';
import { fetchWallets } from '../../api/wallets.js';
import { fetchCategories, fetchAllTags } from '../../api/categories.js';

// Multi-select state (local to modal, committed on Apply)
let draftFilter = {};
let allCategories = [];
let allTags = [];
let multiSelectTarget = null; // 'category' | 'tag'

export function initFilterModal() {
    const modal = document.getElementById('filter-modal');
    const btnOpen = document.getElementById('btn-filter-txns');
    const btnClose = document.getElementById('btn-close-filter');
    const btnApply = document.getElementById('btn-filter-apply');
    const btnReset = document.getElementById('btn-filter-reset');
    if (!modal || !btnOpen) return;

    // Multi-select modal
    const msModal = document.getElementById('filter-multiselect-modal');
    const msTitle = document.getElementById('filter-ms-title');
    const msList  = document.getElementById('filter-ms-list');
    const msApply = document.getElementById('btn-filter-ms-apply');
    const msCancel = document.getElementById('btn-filter-ms-cancel');

    // ---- Mở Filter Modal ----
    btnOpen.addEventListener('click', async () => {
        // Copy current filter to draft
        draftFilter = JSON.parse(JSON.stringify(appState.filterState));

        // Load wallets
        const wallets = await fetchWallets();
        buildWalletDropdown(wallets);

        // Load categories (all types)
        allCategories = await fetchCategories('expense');
        const incomeCategories = await fetchCategories('income');
        const seen = new Set(allCategories.map(c => c.id));
        incomeCategories.forEach(c => { if (!seen.has(c.id)) { allCategories.push(c); seen.add(c.id); } });

        // Load all tags
        allTags = await fetchAllTags();

        renderFilterModal();

        modal.classList.remove('hidden');
        modal.classList.add('flex');
    });

    const closeFilter = () => {
        modal.classList.add('hidden');
        modal.classList.remove('flex');
    };

    btnClose.addEventListener('click', closeFilter);
    modal.addEventListener('click', (e) => { if (e.target === modal) closeFilter(); });

    // ---- Apply Filter ----
    btnApply.addEventListener('click', () => {
        // Commit draft → appState
        appState.filterState = JSON.parse(JSON.stringify(draftFilter));
        closeFilter();
        // Notify transactionsTab to re-render
        window.dispatchEvent(new CustomEvent('filtersChanged'));
        updateFilterBadge();
    });

    // ---- Reset Filter ----
    btnReset.addEventListener('click', () => {
        draftFilter = {
            keyword: '', type: 'all', walletId: null,
            useDateRange: false, dateFrom: null, dateTo: null,
            categoryIds: [], tagIds: []
        };
        renderFilterModal();
    });

    // ---- Keyword Input ----
    document.getElementById('filter-keyword')?.addEventListener('input', (e) => {
        draftFilter.keyword = e.target.value;
    });

    // ---- Type Buttons ----
    document.querySelectorAll('.filter-type-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            draftFilter.type = btn.dataset.type;
            document.querySelectorAll('.filter-type-btn').forEach(b => {
                b.classList.toggle('bg-primary', b.dataset.type === draftFilter.type);
                b.classList.toggle('text-white', b.dataset.type === draftFilter.type);
                b.classList.toggle('bg-gray-700', b.dataset.type !== draftFilter.type);
                b.classList.toggle('text-gray-300', b.dataset.type !== draftFilter.type);
            });
        });
    });

    // ---- Wallet Select ----
    document.getElementById('filter-wallet-select')?.addEventListener('change', (e) => {
        draftFilter.walletId = e.target.value || null;
    });

    // ---- Date Range Toggle ----
    document.getElementById('filter-date-checkbox')?.addEventListener('change', (e) => {
        draftFilter.useDateRange = e.target.checked;
        const dateFields = document.getElementById('filter-date-fields');
        if (dateFields) dateFields.classList.toggle('hidden', !e.target.checked);
    });

    document.getElementById('filter-date-from')?.addEventListener('change', (e) => {
        draftFilter.dateFrom = e.target.value || null;
    });
    document.getElementById('filter-date-to')?.addEventListener('change', (e) => {
        draftFilter.dateTo = e.target.value || null;
    });

    // ---- Category Multi-select Button ----
    document.getElementById('btn-filter-select-categories')?.addEventListener('click', async () => {
        multiSelectTarget = 'category';
        msTitle.innerText = 'Select Categories';
        renderMultiSelectList(allCategories, draftFilter.categoryIds);
        msModal.classList.remove('hidden');
        msModal.classList.add('flex');
    });

    // ---- Tag Multi-select Button (dynamic, based on selected categories) ----
    document.getElementById('btn-filter-select-tags')?.addEventListener('click', async () => {
        multiSelectTarget = 'tag';
        msTitle.innerText = 'Select Tags';
        // Collect all tags from selected categories (or all if none selected)
        const tagsToShow = allTags;
        renderMultiSelectList(tagsToShow, draftFilter.tagIds, true /* isTag */);
        msModal.classList.remove('hidden');
        msModal.classList.add('flex');
    });

    // ---- Multi-select modal Apply ----
    msApply.addEventListener('click', () => {
        const checked = [...document.querySelectorAll('.ms-item-checkbox:checked')].map(cb => cb.value);
        if (multiSelectTarget === 'category') {
            draftFilter.categoryIds = checked;
            updateCategoryTagSummary();
        } else {
            draftFilter.tagIds = checked;
            updateCategoryTagSummary();
        }
        msModal.classList.add('hidden');
        msModal.classList.remove('flex');
    });

    msCancel.addEventListener('click', () => {
        msModal.classList.add('hidden');
        msModal.classList.remove('flex');
    });
}

function buildWalletDropdown(wallets) {
    const sel = document.getElementById('filter-wallet-select');
    if (!sel) return;
    sel.innerHTML = '<option value="">All Wallets</option>';
    wallets.forEach(w => {
        const opt = document.createElement('option');
        opt.value = w.id;
        opt.textContent = w.name;
        sel.appendChild(opt);
    });
    sel.value = draftFilter.walletId || '';
}

function renderFilterModal() {
    // Keyword
    const kwInput = document.getElementById('filter-keyword');
    if (kwInput) kwInput.value = draftFilter.keyword;

    // Type buttons
    document.querySelectorAll('.filter-type-btn').forEach(b => {
        const active = b.dataset.type === draftFilter.type;
        b.classList.toggle('bg-primary', active);
        b.classList.toggle('text-white', active);
        b.classList.toggle('bg-gray-700', !active);
        b.classList.toggle('text-gray-300', !active);
    });

    // Wallet
    const sel = document.getElementById('filter-wallet-select');
    if (sel) sel.value = draftFilter.walletId || '';

    // Date
    const cb = document.getElementById('filter-date-checkbox');
    if (cb) cb.checked = draftFilter.useDateRange;
    const df = document.getElementById('filter-date-fields');
    if (df) df.classList.toggle('hidden', !draftFilter.useDateRange);
    const fromI = document.getElementById('filter-date-from');
    const toI = document.getElementById('filter-date-to');
    if (fromI) fromI.value = draftFilter.dateFrom || '';
    if (toI) toI.value = draftFilter.dateTo || '';

    updateCategoryTagSummary();
}

function updateCategoryTagSummary() {
    const catBtn = document.getElementById('btn-filter-select-categories');
    const tagBtn = document.getElementById('btn-filter-select-tags');
    if (catBtn) {
        const count = draftFilter.categoryIds?.length || 0;
        catBtn.innerText = count === 0 ? 'All Categories' : `${count} selected`;
    }
    if (tagBtn) {
        const count = draftFilter.tagIds?.length || 0;
        tagBtn.innerText = count === 0 ? 'All Tags' : `${count} selected`;
    }
}

function renderMultiSelectList(items, selectedIds, isTag = false) {
    const msList = document.getElementById('filter-ms-list');
    if (!msList) return;
    msList.innerHTML = '';
    if (!items || items.length === 0) {
        msList.innerHTML = '<p class="text-sm text-gray-500 text-center py-4">No items available</p>';
        return;
    }
    items.forEach(item => {
        const label = document.createElement('label');
        label.className = 'flex items-center gap-3 p-3 rounded-xl bg-darkbg border border-gray-800 cursor-pointer hover:border-primary transition';
        label.innerHTML = `
            <input type="checkbox" class="ms-item-checkbox w-5 h-5 accent-primary" value="${item.id}" ${selectedIds.includes(item.id) ? 'checked' : ''}>
            <span class="text-sm text-white font-medium">${item.name}</span>
        `;
        msList.appendChild(label);
    });
}

// Cập nhật badge trên nút Filter
export function updateFilterBadge() {
    const badge = document.getElementById('filter-active-badge');
    if (!badge) return;
    const f = appState.filterState;
    const active = f.keyword || f.type !== 'all' || f.walletId || f.useDateRange || f.categoryIds?.length || f.tagIds?.length;
    badge.classList.toggle('hidden', !active);
}

// Hàm apply filter vào danh sách transactions
export function applyFilters(txns) {
    const f = appState.filterState;
    return txns.filter(t => {
        // Transfer linker (hidden by default) — không filter chúng
        // Keyword
        if (f.keyword) {
            const kw = f.keyword.toLowerCase();
            const catName = (t.categories?.name || t.category_name || '').toLowerCase();
            const tagName = (t.tags?.name || t.tag_name || '').toLowerCase();
            const remark = (t.remark || '').toLowerCase();
            const wallet = (t.wallets?.name || '').toLowerCase();
            if (!catName.includes(kw) && !tagName.includes(kw) && !remark.includes(kw) && !wallet.includes(kw)) return false;
        }
        // Type
        if (f.type !== 'all' && t.type !== f.type) return false;
        // Wallet
        if (f.walletId && t.wallet_id !== f.walletId) return false;
        // Date range
        if (f.useDateRange) {
            if (f.dateFrom && t.transaction_date < f.dateFrom) return false;
            if (f.dateTo && t.transaction_date > f.dateTo) return false;
        }
        // Category
        if (f.categoryIds?.length > 0) {
            if (!f.categoryIds.includes(t.category_id)) return false;
        }
        // Tag
        if (f.tagIds?.length > 0) {
            if (!f.tagIds.includes(t.tag_id)) return false;
        }
        return true;
    });
}
